import { useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, FileText, Filter, Rows3 } from 'lucide-react';
import {
  Badge,
  DataTable,
  ListRow,
  Stack,
  Text,
  cn,
  type BadgeVariant,
  type Column,
  type DataTableProps,
} from '@beatq/ui';

export function statusBadgeVariant(status: string | null | undefined): BadgeVariant {
  const s = (status ?? '').toUpperCase();
  if (
    ['OPEN', 'PENDING', 'REVIEW', 'IN_PROGRESS', 'AWAITING_APPROVAL'].some((x) => s.includes(x))
  ) {
    return 'info';
  }
  if (['URGENT', 'HIGH', 'ESCALATED', 'BLOCK', 'BLOCKED', 'DANGER'].some((x) => s.includes(x))) {
    return 'danger';
  }
  if (['WARNING', 'LATE', 'BREACH', 'CAUTION'].some((x) => s.includes(x))) {
    return 'warning';
  }
  if (['RESOLVED', 'CLOSED', 'APPROVED', 'ALLOW', 'ACTIVE', 'SUCCESS'].some((x) => s.includes(x))) {
    return 'success';
  }
  return 'default';
}

function statusDotClass(variant: BadgeVariant): string {
  switch (variant) {
    case 'success':
      return 'bg-ready';
    case 'warning':
      return 'bg-late';
    case 'danger':
      return 'bg-blocked';
    case 'info':
      return 'bg-new';
    default:
      return 'bg-neutral';
  }
}

export function StatusBadge({
  status,
  label,
  showDot = true,
}: {
  status: string | null | undefined;
  /** Visible text when `status` is only used to pick the tone. */
  label?: string;
  showDot?: boolean;
}) {
  if (!status) return null;
  const variant = statusBadgeVariant(status);
  return (
    <Badge
      data-status={status.toUpperCase()}
      variant={variant}
      className={`admin-status admin-status--${variant} inline-flex items-center gap-xs`}
    >
      {showDot ? (
        <span
          className={cn('inline-block h-1.5 w-1.5 rounded-pill', statusDotClass(variant))}
          aria-hidden
        />
      ) : null}
      {label ?? status}
    </Badge>
  );
}

export function QueueList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul
      aria-label={label}
      className="m-0 list-none overflow-hidden rounded-surface border border-line-subtle bg-raised p-0"
    >
      {children}
    </ul>
  );
}

export function QueueListRow({
  title,
  meta,
  status,
  actionLabel,
  href,
  onActivate,
  trailing,
  className,
}: {
  title: ReactNode;
  meta?: ReactNode;
  status?: string | null;
  actionLabel?: string;
  href?: string;
  onActivate?: () => void;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <ListRow
      actionLabel={actionLabel}
      href={href}
      onActivate={onActivate}
      className={cn('hov:bg-sunken odd:bg-raised even:bg-page', className)}
      trailing={trailing ?? (status ? <StatusBadge status={status} /> : undefined)}
    >
      <Text variant="body" className="font-medium">
        {title}
      </Text>
      {meta ? (
        <Text variant="caption" className="text-muted">
          {meta}
        </Text>
      ) : null}
    </ListRow>
  );
}

export function QueueToolbar({
  children,
  resultCount,
}: {
  children?: ReactNode;
  resultCount?: number;
}) {
  return (
    <div className={`admin-queue-toolbar ${children ? 'admin-panel' : 'admin-queue-summary'}`}>
      <div className="admin-toolbar-title">
        {children ? <Filter size={20} /> : <Rows3 size={18} />}
        <div>
          <h2>{children ? 'Filters & actions' : 'Queue overview'}</h2>
          {children ? <p>Refine the view and manage your workspace.</p> : null}
        </div>
        {resultCount !== undefined ? (
          <span className="admin-result-count">{resultCount} results</span>
        ) : null}
      </div>
      {children ? <div className="admin-toolbar-fields">{children}</div> : null}
    </div>
  );
}

export function QueueActions({ children }: { children: ReactNode }) {
  return (
    <Stack
      direction="row"
      gap="inlineGap"
      className="admin-action-row mt-field flex-wrap"
      align="center"
    >
      {children}
    </Stack>
  );
}

/** Shared action row — replaces duplicated local ActionRow helpers. */
export function ActionRow({ children }: { children: ReactNode }) {
  return <QueueActions>{children}</QueueActions>;
}

export function AdminDataTable<T>(props: DataTableProps<T>) {
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const pages = Math.max(1, Math.ceil(props.rows.length / pageSize));
  const activePage = Math.min(page, pages);
  const offset = (activePage - 1) * pageSize;
  return (
    <section className="admin-panel admin-data-panel" aria-label={`${props.caption} workspace`}>
      <div className="admin-panel-title">
        <FileText size={22} />
        <h2>{props.caption}</h2>
        <span className="admin-result-count">
          {props.rows.length} {props.rows.length === 1 ? 'record' : 'records'}
        </span>
      </div>
      <DataTable
        {...props}
        rows={props.rows.slice(offset, offset + pageSize)}
        rowKey={(row, index) =>
          props.rowKey ? props.rowKey(row, index + offset) : String(index + offset)
        }
        className={cn(
          'admin-data-table overflow-x-auto bg-raised [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-[1] [&_thead]:bg-raised',
          '[&_tbody_tr]:hov:bg-sunken [&_th]:whitespace-nowrap [&_td]:align-middle',
          props.className,
        )}
      />
      {props.rows.length > 0 ? (
        <div className="admin-table-footer">
          <span>
            Showing {offset + 1}–{Math.min(offset + pageSize, props.rows.length)} of{' '}
            {props.rows.length}
          </span>
          {pages > 1 ? (
            <div className="admin-pagination">
              <button
                type="button"
                aria-label={`Previous ${props.caption} page`}
                disabled={activePage === 1}
                onClick={() => setPage(activePage - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span>
                {activePage} / {pages}
              </span>
              <button
                type="button"
                aria-label={`Next ${props.caption} page`}
                disabled={activePage === pages}
                onClick={() => setPage(activePage + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/** Token-styled key/value rows (replaces raw ul KeyValueList clones). */
export function KeyValueList({ rows }: { rows: { label: string; value: string }[] }) {
  if (rows.length === 0) return null;
  return (
    <dl className="admin-detail-list">
      {rows.map((row) => (
        <div key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** INV-08 — internal-only region landmark for investigation / payment panels. */
export function InternalOnlyRegion({
  label = 'Internal only',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={label}
      data-inv="INV-08"
      className="mt-field rounded-surface border border-line-strong bg-sunken p-field"
    >
      <Text variant="caption" className="mb-xs font-medium text-muted">
        {label}
      </Text>
      {children}
    </section>
  );
}

export type { Column };
